'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    createTerminal,
    CreateTerminalData,
    deleteTerminal,
    getTerminals,
    PaymentTerminal,
    updateTerminal,
    UpdateTerminalData,
} from '@/lib/api/financial/financial-terminal-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useEffect, useRef, useState } from 'react'
import {
    MdAdd,
    MdArrowBack,
    MdDelete,
    MdEdit,
    MdPointOfSale,
    MdSave,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

interface TerminalForm {
    name: string
    serialNumber: string
    provider: string
    credentialsJson: string
    sandboxMode: boolean
    webhookSecret: string
    active: boolean
}

const initialForm: TerminalForm = {
    name: '',
    serialNumber: '',
    provider: '',
    credentialsJson: '{}',
    sandboxMode: false,
    webhookSecret: '',
    active: true,
}

const PROVIDER_OPTIONS: Array<{ value: string; label: string }> = [
    { value: 'cielo', label: 'Cielo (Lio)' },
    { value: 'stone', label: 'Stone' },
    { value: 'pagseguro_pos', label: 'PagSeguro (Moderninha)' },
    { value: 'rede', label: 'Rede' },
    { value: 'getnet', label: 'Getnet' },
]

const providerLabel = (provider: string): string => {
    const opt = PROVIDER_OPTIONS.find(o => o.value === provider)
    return opt ? opt.label : provider.charAt(0).toUpperCase() + provider.slice(1)
}

export default function TerminalsPage() {
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('financial', 'terminals')

    const [terminals, setTerminals] = useState<PaymentTerminal[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    const modalRef = useRef<HTMLDialogElement>(null)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [form, setForm] = useState<TerminalForm>(initialForm)

    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        loadData()
    }, [])

    const loadData = async () => {
        setLoading(true)
        try {
            const res = await getTerminals()
            if (res.success && res.result) {
                setTerminals(res.result)
            }
        } catch {
            toast.error('Erro ao carregar terminais')
        } finally {
            setLoading(false)
        }
    }

    const openCreateModal = () => {
        setEditingId(null)
        setForm(initialForm)
        modalRef.current?.showModal()
    }

    const openEditModal = (terminal: PaymentTerminal) => {
        setEditingId(terminal.id)
        setForm({
            name: terminal.name,
            serialNumber: terminal.serialNumber,
            provider: terminal.provider,
            credentialsJson: JSON.stringify(terminal.credentials ?? {}, null, 2),
            sandboxMode: terminal.sandboxMode ?? false,
            webhookSecret: terminal.webhookSecret ?? '',
            active: terminal.active,
        })
        modalRef.current?.showModal()
    }

    const closeModal = () => {
        modalRef.current?.close()
        setEditingId(null)
        setForm(initialForm)
    }

    const parseCredentials = (): Record<string, string> | null => {
        try {
            const parsed = JSON.parse(form.credentialsJson || '{}')
            if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
                return null
            }
            return parsed as Record<string, string>
        } catch {
            return null
        }
    }

    const handleSave = async () => {
        if (!form.name.trim()) {
            toast.warning('Informe o nome do terminal')
            return
        }
        if (!form.serialNumber.trim()) {
            toast.warning('Informe o número serial')
            return
        }
        if (!form.provider.trim()) {
            toast.warning('Selecione o provider do terminal')
            return
        }
        const credentials = parseCredentials()
        if (credentials === null) {
            toast.warning('Credenciais devem ser um JSON válido (objeto)')
            return
        }

        setSaving(true)
        try {
            const payload: CreateTerminalData = {
                name: form.name.trim(),
                serialNumber: form.serialNumber.trim(),
                provider: form.provider.trim(),
                credentials,
                sandboxMode: form.sandboxMode,
                webhookSecret: form.webhookSecret.trim() || undefined,
                active: form.active,
            }

            const res = editingId
                ? await updateTerminal(editingId, payload as UpdateTerminalData)
                : await createTerminal(payload)

            if (res.success) {
                toast.success(editingId ? 'Terminal atualizado com sucesso' : 'Terminal criado com sucesso')
                closeModal()
                loadData()
            } else {
                toast.error(res.error || 'Erro ao salvar terminal')
            }
        } catch {
            toast.error('Erro ao salvar terminal')
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteId) return
        setDeleting(true)
        try {
            const res = await deleteTerminal(deleteId)
            if (res.success) {
                toast.success('Terminal excluído com sucesso')
                setDeleteId(null)
                loadData()
            } else {
                toast.error(res.error || 'Erro ao excluir terminal')
            }
        } catch {
            toast.error('Erro ao excluir terminal')
        } finally {
            setDeleting(false)
        }
    }

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
                        <MdArrowBack size={20} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold">Terminais de Pagamento</h1>
                        <p className="text-base-content/60 text-sm mt-1">
                            Cada terminal POS possui sua própria credencial — independente do provider de cobrança digital.
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button className="btn btn-secondary" onClick={openCreateModal}>
                        <MdAdd size={20} />
                        Novo Terminal
                    </button>
                )}
            </div>

            {loading && (
                <div className="flex items-center justify-center min-h-[300px]">
                    <div className="loading loading-spinner loading-lg text-secondary"></div>
                </div>
            )}

            {!loading && terminals.length === 0 && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body items-center text-center py-16">
                        <MdPointOfSale size={48} className="text-base-content/30" />
                        <p className="text-base-content/60 mt-2">Nenhum terminal cadastrado</p>
                        {canCreate && (
                            <button className="btn btn-secondary btn-sm mt-4" onClick={openCreateModal}>
                                <MdAdd size={18} />
                                Cadastrar Terminal
                            </button>
                        )}
                    </div>
                </div>
            )}

            {!loading && terminals.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {terminals.map(terminal => (
                        <div
                            key={terminal.id}
                            className="card bg-base-100 shadow hover:shadow-md transition-shadow"
                        >
                            <div className="card-body p-4">
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-secondary/10 rounded-lg">
                                        <MdPointOfSale size={24} className="text-secondary" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-base truncate">{terminal.name}</h3>
                                        <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-base-content/60">
                                            <span>Serial: {terminal.serialNumber}</span>
                                            <span>|</span>
                                            <span>Provider: {providerLabel(terminal.provider)}</span>
                                            {terminal.sandboxMode && (
                                                <>
                                                    <span>|</span>
                                                    <span className="badge badge-xs badge-warning">Sandbox</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div
                                        className={`badge badge-sm ${terminal.active ? 'badge-success' : 'badge-ghost'}`}
                                    >
                                        {terminal.active ? 'Ativo' : 'Inativo'}
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-base-200">
                                    {canUpdate && (
                                        <button className="btn btn-ghost btn-sm" onClick={() => openEditModal(terminal)}>
                                            <MdEdit size={16} />
                                            Editar
                                        </button>
                                    )}
                                    {canDelete && (
                                        <button
                                            className="btn btn-ghost btn-sm text-error"
                                            onClick={() => setDeleteId(terminal.id)}
                                        >
                                            <MdDelete size={16} />
                                            Excluir
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <dialog ref={modalRef} className="modal">
                <div className="modal-box !max-w-lg">
                    <h3 className="font-bold text-lg mb-4">
                        {editingId ? 'Editar Terminal' : 'Novo Terminal'}
                    </h3>

                    <div className="space-y-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Nome</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Ex: Terminal Recepção"
                                value={form.name}
                                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Número Serial</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Ex: SN-12345678"
                                value={form.serialNumber}
                                onChange={e => setForm(prev => ({ ...prev, serialNumber: e.target.value }))}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Provider</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={form.provider}
                                onChange={e => setForm(prev => ({ ...prev, provider: e.target.value }))}
                            >
                                <option value="">Selecione um provider</option>
                                {PROVIDER_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Credenciais (JSON)</span>
                                <span className="label-text-alt text-base-content/50">
                                    Ex: {`{ "merchantId": "...", "merchantKey": "..." }`}
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full font-mono text-sm"
                                rows={5}
                                value={form.credentialsJson}
                                onChange={e => setForm(prev => ({ ...prev, credentialsJson: e.target.value }))}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Webhook Secret (opcional)</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                value={form.webhookSecret}
                                onChange={e => setForm(prev => ({ ...prev, webhookSecret: e.target.value }))}
                            />
                        </div>

                        <div className="flex items-center gap-6">
                            <label className="label cursor-pointer gap-3">
                                <input
                                    type="checkbox"
                                    className="toggle toggle-secondary"
                                    checked={form.sandboxMode}
                                    onChange={e => setForm(prev => ({ ...prev, sandboxMode: e.target.checked }))}
                                />
                                <span className="label-text">Sandbox</span>
                            </label>

                            <label className="label cursor-pointer gap-3">
                                <input
                                    type="checkbox"
                                    className="toggle toggle-secondary"
                                    checked={form.active}
                                    onChange={e => setForm(prev => ({ ...prev, active: e.target.checked }))}
                                />
                                <span className="label-text">Ativo</span>
                            </label>
                        </div>
                    </div>

                    <div className="modal-action">
                        <button className="btn btn-ghost" onClick={closeModal} disabled={saving}>
                            Cancelar
                        </button>
                        <button className="btn btn-secondary" onClick={handleSave} disabled={saving}>
                            {saving ? (
                                <span className="loading loading-spinner loading-sm"></span>
                            ) : (
                                <MdSave size={18} />
                            )}
                            {editingId ? 'Salvar' : 'Criar'}
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={closeModal}>close</button>
                </form>
            </dialog>

            <ConfirmModal
                isOpen={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title="Excluir Terminal"
                message="Tem certeza de que deseja excluir este terminal? Esta ação não pode ser desfeita."
                loading={deleting}
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
